// Time zone helpers built on Intl.DateTimeFormat#formatToParts with an explicit
// timeZone and hourCycle, so the output does not depend on the locale data of
// the browser or Node. Dates in and out are UTC instants.

export const SYDNEY = 'Australia/Sydney';

export const POI_TIMEZONE = Object.freeze({
  WA: 'Australia/Perth',
  SA: 'Australia/Adelaide',
  NSW: SYDNEY,
  ACT: SYDNEY,
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  TAS: 'Australia/Hobart',
  NT: 'Australia/Darwin',
});

export const ZONE_LABEL = Object.freeze({
  'Australia/Sydney': 'SYD',
  'Australia/Perth': 'PER',
  'Australia/Adelaide': 'ADL',
  'Australia/Melbourne': 'MEL',
  'Australia/Brisbane': 'BNE',
  'Australia/Hobart': 'HBA',
  'Australia/Darwin': 'DRW',
});

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const NO_ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;

const formatters = {};

function zoneParts (date, zone) {
  if (!formatters[zone]) {
    formatters[zone] = new Intl.DateTimeFormat('en-AU', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  const parts = {};
  formatters[zone].formatToParts(date).forEach(p => { parts[p.type] = p.value; });
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

// The server stores UTC; a timestamp without a zone designator is UTC, not
// the browser's local time.
export function toDate (value) {
  if (value === null || value === undefined || value === '') return null;
  let v = value;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && !NO_ZONE_SUFFIX.test(v)) v += 'Z';
  const date = v instanceof Date ? v : new Date(v);
  return isNaN(date.getTime()) ? null : date;
}

const pad = (n) => String(n).padStart(2, '0');

export function zoneOffsetMinutes (date, zone) {
  const p = zoneParts(date, zone);
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const wholeSecond = Math.floor(date.getTime() / 1000) * 1000;
  return Math.round((wallAsUtc - wholeSecond) / MINUTE_MS);
}

// Two passes: the offset is read at the wall clock taken as UTC, then again at
// the result, so a date on either side of a daylight saving change resolves.
export function wallClockToUtc (wall, zone) {
  const d = wall && /^(\d{4})-(\d{2})-(\d{2})$/.exec(wall.date || '');
  const t = wall && /^(\d{2}):(\d{2})$/.exec(wall.time || '');
  if (!d || !t) return null;
  const guess = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  const first = zoneOffsetMinutes(new Date(guess), zone);
  let result = guess - first * MINUTE_MS;
  const second = zoneOffsetMinutes(new Date(result), zone);
  if (second !== first) result = guess - second * MINUTE_MS;
  return new Date(result);
}

export function formatInZone (utc, zone, { year = false } = {}) {
  const date = toDate(utc);
  if (!date) return '';
  const p = zoneParts(date, zone);
  const day = year ? `${pad(p.day)}/${pad(p.month)}/${p.year}` : `${pad(p.day)}/${pad(p.month)}`;
  return `${day} ${pad(p.hour)}:${pad(p.minute)}`;
}

// "10:06:09" in the zone, for "last refresh" style clocks.
export function formatClockInZone (utc, zone) {
  const date = toDate(utc);
  if (!date) return '';
  const p = zoneParts(date, zone);
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

// "14/10 10:06 SYD . 07:06 PER"; one zone when the POI keeps Sydney time.
// The POI part carries its date only when that differs from the Sydney date.
export function formatDual (utc, poiState) {
  const date = toDate(utc);
  if (!date) return '';
  const sydney = formatInZone(date, SYDNEY);
  const sydneyText = `${sydney} ${ZONE_LABEL[SYDNEY]}`;
  const zone = POI_TIMEZONE[poiState];
  if (!zone) return sydneyText;
  const poi = formatInZone(date, zone);
  if (poi === sydney) return sydneyText;
  const [sydneyDay] = sydney.split(' ');
  const [poiDay, poiTime] = poi.split(' ');
  return `${sydneyText} . ${poiDay === sydneyDay ? poiTime : poi} ${ZONE_LABEL[zone]}`;
}

export const OUTSIDE_HOURS = 'Outside Mon-Fri 09:00-18:00 Sydney';
export const TOO_SOON = 'Less than 3 business days ahead';
export const END_BEFORE_START = 'End must be after start';
const BUSINESS_START_MIN = 9 * 60;
const BUSINESS_END_MIN = 18 * 60;
const MIN_BUSINESS_DAYS = 3;

const dayNumber = (p) => Date.UTC(p.year, p.month - 1, p.day) / DAY_MS;
const isWeekday = (dayNo) => {
  const dow = new Date(dayNo * DAY_MS).getUTCDay();
  return dow !== 0 && dow !== 6;
};

// Weekdays after today's Sydney date up to and including the start date.
// Public holidays are not known to the page.
function businessDaysAhead (now, start) {
  const from = dayNumber(zoneParts(now, SYDNEY));
  const to = dayNumber(zoneParts(start, SYDNEY));
  let count = 0;
  for (let day = from + 1; day <= to; day++) {
    if (isWeekday(day)) count++;
  }
  return count;
}

// Optus books Sydney time, Mon-Fri 09:00-18:00, 3 business days ahead.
// The warnings never block.
export function windowWarnings (startUtc, endUtc, nowUtc) {
  const start = toDate(startUtc);
  const end = toDate(endUtc);
  if (!start || !end) return [];
  if (end <= start) return [END_BEFORE_START];
  const warnings = [];
  const s = zoneParts(start, SYDNEY);
  const e = zoneParts(end, SYDNEY);
  const sameDay = dayNumber(s) === dayNumber(e);
  const startMin = s.hour * 60 + s.minute;
  const endMin = e.hour * 60 + e.minute;
  if (!sameDay || !isWeekday(dayNumber(s)) || startMin < BUSINESS_START_MIN || endMin > BUSINESS_END_MIN) {
    warnings.push(OUTSIDE_HOURS);
  }
  const now = toDate(nowUtc);
  if (now && businessDaysAhead(now, start) < MIN_BUSINESS_DAYS) warnings.push(TOO_SOON);
  return warnings;
}

function duration (ms) {
  const total = Math.max(1, Math.round(ms / MINUTE_MS));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

// kind: none | future | open | ended
export function relativeWindow (startUtc, endUtc, nowUtc) {
  const start = toDate(startUtc);
  const end = toDate(endUtc);
  if (!start || !end) return { kind: 'none', text: 'No window set' };
  const now = toDate(nowUtc) || new Date();
  if (now < start) return { kind: 'future', text: `in ${duration(start - now)}` };
  if (now < end) return { kind: 'open', text: `open now, ${Math.ceil((end - now) / MINUTE_MS)} min left` };
  const ago = now - end;
  return { kind: 'ended', text: ago < MINUTE_MS ? 'ended just now' : `ended ${duration(ago)} ago` };
}
